import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

import SettaSwitch from '../../components/settaSwitch';
import {
    formatBackendDatePtBr,
    formatMonthShortPtBr,
    formatMonthYearPtBr,
    getMonthKey,
    parseBackendDate
} from '../../utils/dateUtils';
import {
    formatDecimal,
    formatInteger,
    formatPercentRatio,
    getNiceMax
} from '../../utils/numberUtils';
import '../css/falhasBissCompare.css';

export const maxFocusedInverters = 4;

const metricOptions = [
    { value: 'ratio', label: 'Real ÷ Estimado' },
    { value: 'perda', label: 'Perda' },
    { value: 'geracao', label: 'Geração' }
];

const slotColor = (slot) => `var(--falhas-biss-inv-${slot + 1})`;

const getShortDateLabel = (value) => {
    const date = parseBackendDate(value);
    return date ? String(date.getDate()).padStart(2, '0') : value;
};

const getPeriodKey = (data, isMonthly) => (isMonthly ? getMonthKey(data) : data);

const buildPeriods = (dayRows, isMonthly) => {
    const dayKeys = dayRows.map((row) => row.data);

    if (!isMonthly) {
        return dayKeys.map((key, index) => ({
            key,
            index,
            label: getShortDateLabel(key),
            tooltip: formatBackendDatePtBr(key, key),
            showLabel: dayKeys.length <= 12 || index % 5 === 0 || index === dayKeys.length - 1
        }));
    }

    const monthKeys = [...new Set(dayKeys.map(getMonthKey).filter(Boolean))].sort();

    return monthKeys.map((key, index) => ({
        key,
        index,
        label: formatMonthShortPtBr(`${key}-01`),
        tooltip: formatMonthYearPtBr(`${key}-01`),
        showLabel: true
    }));
};

const emptyValues = () => ({ geracao: 0, perda: 0, estimado: 0 });

const buildInverters = ({ alertas, falhas, inversores, isMonthly, rows }) => {
    const inverters = new Map();
    const infoById = new Map(inversores.map((info) => [info.inversorId, info]));

    rows.forEach((row) => {
        const info = infoById.get(row.inversorId);
        const inverter = inverters.get(row.inversorId) ?? {
            id: row.inversorId,
            nome: info?.inversor ?? `Inversor ${row.inversorId}`,
            serial: info?.serial ?? '',
            values: new Map(),
            faultDays: new Set(),
            faultPeriods: new Set(),
            alertCount: 0,
            totals: emptyValues()
        };
        const periodKey = getPeriodKey(row.data, isMonthly);
        const current = inverter.values.get(periodKey) ?? emptyValues();

        inverter.values.set(periodKey, {
            geracao: current.geracao + row.geracao,
            perda: current.perda + row.perda,
            estimado: current.estimado + row.estimado
        });
        inverter.totals.geracao += row.geracao;
        inverter.totals.perda += row.perda;
        inverter.totals.estimado += row.estimado;
        inverters.set(row.inversorId, inverter);
    });

    falhas.forEach((falha) => {
        const inverter = inverters.get(falha.inversorId);

        if (!inverter) return;

        inverter.faultDays.add(falha.data);
        inverter.faultPeriods.add(getPeriodKey(falha.data, isMonthly));
    });

    alertas.forEach((alerta) => {
        const inverter = inverters.get(alerta.inversorId);

        if (inverter) {
            inverter.alertCount += 1;
        }
    });

    return [...inverters.values()].map((inverter) => ({
        ...inverter,
        ratio: inverter.totals.estimado > 0 ? inverter.totals.geracao / inverter.totals.estimado : 0
    }));
};

const getRatioStatus = (ratio) => {
    if (ratio >= 1) return 'is-good';
    if (ratio >= 0.95) return 'is-warning';
    return 'is-bad';
};

const getPlotValue = (value) => (Number.isFinite(value) && value > 0 ? value : 0);

const getValues = (inverter, periodKey) => inverter.values.get(periodKey) ?? emptyValues();

const getMetricValue = (values, metric) => {
    if (metric === 'ratio') {
        return values.estimado > 0 ? values.geracao / values.estimado : 0;
    }

    return getPlotValue(values[metric]);
};

// Geometria dos SVGs (unidades do viewBox).
const smallChart = { width: 560, height: 250, left: 50, right: 12, top: 12, bottom: 24 };
const overlayChart = { width: 1100, height: 300, left: 56, right: 150, top: 12, bottom: 24 };

const getGeometry = (chart) => ({
    ...chart,
    innerWidth: chart.width - chart.left - chart.right,
    innerHeight: chart.height - chart.top - chart.bottom
});

const yTickRatios = [0, 0.25, 0.5, 0.75, 1];

// Acima de dois meses o comparativo abre agrupado por mês (o switch continua disponível).
const longPeriodDays = 62;

const FalhasBissCompare = ({ alertas, dayRows, falhas, hasScope = true, inversores = [], isLoading, rows }) => {
    const [focused, setFocused] = useState([]);
    const [view, setView] = useState('multiples');
    const [metric, setMetric] = useState('ratio');
    const [isMonthly, setIsMonthly] = useState(false);
    const [hotIndex, setHotIndex] = useState(null);
    const [notice, setNotice] = useState('');
    const noticeTimeout = useRef(null);
    const availableIdsRef = useRef(new Set());

    const periods = useMemo(() => buildPeriods(dayRows, isMonthly), [dayRows, isMonthly]);
    const inverters = useMemo(
        () => buildInverters({ alertas, falhas, inversores, isMonthly, rows }),
        [alertas, falhas, inversores, isMonthly, rows]
    );

    useEffect(() => {
        setHotIndex(null);
        setIsMonthly(dayRows.length > longPeriodDays);
    }, [dayRows.length]);

    // O foco só é preenchido (até o limite) quando o conjunto de inversores do filtro muda,
    // priorizando os que acabaram de entrar. Trocar só o período não mexe na seleção.
    useEffect(() => {
        const previousAvailable = availableIdsRef.current;
        const availableIds = inverters.map((inverter) => inverter.id);
        const available = new Set(availableIds);
        const setChanged = availableIds.length !== previousAvailable.size ||
            availableIds.some((id) => !previousAvailable.has(id));

        availableIdsRef.current = available;

        setFocused((previous) => {
            const next = previous.filter((entry) => available.has(entry.id));

            if (setChanged) {
                const focusedIds = new Set(next.map((entry) => entry.id));
                const usedSlots = new Set(next.map((entry) => entry.slot));
                const candidates = [
                    ...availableIds.filter((id) => !previousAvailable.has(id)),
                    ...availableIds.filter((id) => previousAvailable.has(id))
                ];

                candidates.forEach((id) => {
                    if (next.length >= maxFocusedInverters || focusedIds.has(id)) return;

                    const freeSlot = [0, 1, 2, 3].find((slot) => !usedSlots.has(slot));

                    usedSlots.add(freeSlot);
                    focusedIds.add(id);
                    next.push({ id, slot: freeSlot });
                });
            }

            const unchanged = next.length === previous.length &&
                next.every((entry, index) => entry.id === previous[index].id);

            return unchanged ? previous : next;
        });
    }, [inverters]);

    useEffect(() => () => clearTimeout(noticeTimeout.current), []);

    const focusedInverters = useMemo(() => focused
        .map((entry) => {
            const inverter = inverters.find((item) => item.id === entry.id);
            return inverter ? { ...inverter, slot: entry.slot } : null;
        })
        .filter(Boolean), [focused, inverters]);

    const sharedMax = useMemo(() => getNiceMax(Math.max(
        0,
        ...focusedInverters.flatMap((inverter) => periods.map((period) => {
            const values = getValues(inverter, period.key);
            return Math.max(getPlotValue(values.geracao) + getPlotValue(values.perda), getPlotValue(values.estimado));
        }))
    )), [focusedInverters, periods]);

    const showNotice = (message) => {
        clearTimeout(noticeTimeout.current);
        setNotice(message);
        noticeTimeout.current = setTimeout(() => setNotice(''), 3200);
    };

    const toggleFocus = (inverter) => {
        const isFocused = focused.some((entry) => entry.id === inverter.id);

        if (isFocused) {
            if (focused.length === 1) {
                showNotice('Mantenha ao menos um inversor nos gráficos.');
                return;
            }

            setFocused(focused.filter((entry) => entry.id !== inverter.id));
            return;
        }

        if (focused.length >= maxFocusedInverters) {
            showNotice(`Máximo de ${maxFocusedInverters} inversores nos gráficos. Remova um para adicionar ${inverter.nome}.`);
            return;
        }

        const usedSlots = new Set(focused.map((entry) => entry.slot));
        const freeSlot = [0, 1, 2, 3].find((slot) => !usedSlots.has(slot));

        setFocused([...focused, { id: inverter.id, slot: freeSlot }]);
    };

    const handleGroupingChange = (checked) => {
        setHotIndex(null);
        setIsMonthly(checked);
    };

    const handleViewChange = (nextView) => {
        setHotIndex(null);
        setView(nextView);
    };

    const hotPeriod = hotIndex === null ? null : periods[hotIndex] ?? null;

    if (!hasScope) {
        return (
            <Box className='falhas-biss-chart-panel falhas-biss-compare'>
                <Box className='falhas-biss-section-heading'>
                    <span><InsightsRoundedIcon /> Comparativo de inversores</span>
                </Box>
                <p className='falhas-biss-compare-empty'>
                    Selecione uma usina ou inversores no filtro para comparar. Site e SPE sozinhos não delimitam o comparativo.
                </p>
            </Box>
        );
    }

    if (inverters.length === 0) {
        return (
            <Box className='falhas-biss-chart-panel falhas-biss-compare'>
                <Box className='falhas-biss-section-heading'>
                    <span><InsightsRoundedIcon /> Comparativo de inversores</span>
                </Box>
                <p className='falhas-biss-compare-empty'>
                    {isLoading ? 'Carregando dados por inversor...' : 'Nenhum dado por inversor no período e filtros selecionados.'}
                </p>
            </Box>
        );
    }

    const renderTooltip = () => {
        if (!hotPeriod || focusedInverters.length === 0) return null;

        const sorted = [...focusedInverters].sort((left, right) => {
            const leftValues = getValues(left, hotPeriod.key);
            const rightValues = getValues(right, hotPeriod.key);
            return getMetricValue(rightValues, 'perda') - getMetricValue(leftValues, 'perda');
        });

        return (
            <Box className='falhas-biss-chart-tooltip falhas-biss-compare-tooltip'>
                <span>{hotPeriod.tooltip}</span>
                {sorted.map((inverter) => {
                    const values = getValues(inverter, hotPeriod.key);
                    const hasFault = inverter.faultPeriods.has(hotPeriod.key);

                    return (
                        <Box className='falhas-biss-compare-tooltip-row' key={inverter.id} style={{ '--inv-color': slotColor(inverter.slot) }}>
                            <i />
                            <strong>{inverter.nome}{hasFault && <small> · falha cadastrada</small>}</strong>
                            <span className='falhas-biss-compare-tooltip-values'>
                                <strong>{formatDecimal(values.geracao)} kWh · {formatPercentRatio(getMetricValue(values, 'ratio'))}</strong>
                                {values.perda > 0 && <em>perda {formatDecimal(values.perda)} kWh</em>}
                                <em className='is-muted'>estimado {formatDecimal(values.estimado)} kWh</em>
                            </span>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderHitAreas = (geometry, className = '') => periods.map((period) => (
        <rect
            className={`falhas-biss-compare-hit ${className}`}
            key={period.key}
            x={geometry.left + period.index * (geometry.innerWidth / periods.length)}
            y={geometry.top}
            width={geometry.innerWidth / periods.length}
            height={geometry.innerHeight}
            onMouseEnter={() => setHotIndex(period.index)}
        />
    ));

    const renderPeriodLabels = (geometry, xOf) => periods.map((period) => (
        period.showLabel
            ? (
                <text
                    className={`falhas-biss-compare-axis-label${hotIndex === period.index ? ' is-hot' : ''}`}
                    key={period.key}
                    x={xOf(period.index)}
                    y={geometry.height - 6}
                    textAnchor='middle'
                >
                    {period.label}
                </text>
            )
            : null
    ));

    const renderSmallChart = (inverter) => {
        const geometry = getGeometry(smallChart);
        const columnWidth = Math.max(3, (geometry.innerWidth / periods.length) - 3);
        const xOf = (index) => geometry.left + (index + 0.5) * (geometry.innerWidth / periods.length);
        const yOf = (value) => geometry.top + geometry.innerHeight - (sharedMax === 0 ? 0 : (value / sharedMax) * geometry.innerHeight);
        const estimatedPath = periods.map((period, index) => (
            `${index === 0 ? 'M' : 'L'} ${xOf(index)} ${yOf(getPlotValue(getValues(inverter, period.key).estimado))}`
        )).join(' ');
        const color = slotColor(inverter.slot);

        return (
            <Box className='falhas-biss-compare-small' key={inverter.id} style={{ '--inv-color': color }}>
                <Box className='falhas-biss-compare-small-heading'>
                    <i />
                    <strong>{inverter.nome}</strong>
                    <small>{inverter.serial}</small>
                    <span className={`falhas-biss-compare-pill ${getRatioStatus(inverter.ratio)}`}>{formatPercentRatio(inverter.ratio)}</span>
                </Box>
                <svg
                    className={`falhas-biss-compare-svg${hotIndex !== null ? ' has-hot' : ''}`}
                    viewBox={`0 0 ${geometry.width} ${geometry.height}`}
                    role='img'
                    aria-label={`Geração e perda de ${inverter.nome} comparadas ao estimado`}
                >
                    {yTickRatios.map((ratio) => (
                        <g key={ratio}>
                            <line className='falhas-biss-compare-grid' x1={geometry.left} x2={geometry.width - geometry.right} y1={yOf(sharedMax * ratio)} y2={yOf(sharedMax * ratio)} />
                            <text x={geometry.left - 6} y={yOf(sharedMax * ratio) + 4} textAnchor='end'>{formatInteger(sharedMax * ratio)}</text>
                        </g>
                    ))}
                    {periods.map((period) => {
                        const values = getValues(inverter, period.key);
                        const generation = getPlotValue(values.geracao);
                        const loss = getPlotValue(values.perda);
                        const generationHeight = yOf(0) - yOf(generation);
                        const lossHeight = yOf(0) - yOf(loss);
                        const x = xOf(period.index) - columnWidth / 2;
                        const generationTop = yOf(generation);
                        const lossTop = generationTop - lossHeight - (lossHeight > 1 ? 2 : 0);

                        return (
                            <g className={`falhas-biss-compare-column${hotIndex === period.index ? ' is-hot' : ''}`} key={period.key}>
                                {generationHeight > 0 && (
                                    <rect x={x} y={generationTop} width={columnWidth} height={generationHeight} rx='2' style={{ fill: color }} />
                                )}
                                {lossHeight > 1 && (
                                    <rect className='is-loss' x={x} y={lossTop} width={columnWidth} height={lossHeight} rx='2' />
                                )}
                                {inverter.faultPeriods.has(period.key) && (
                                    <path className='falhas-biss-compare-fault' d={`M ${xOf(period.index)} ${geometry.top} l 4 7 h -8 z`} />
                                )}
                            </g>
                        );
                    })}
                    <path className='falhas-biss-compare-estimated' d={estimatedPath} />
                    {renderPeriodLabels(geometry, xOf)}
                    {renderHitAreas(geometry)}
                </svg>
            </Box>
        );
    };

    const renderOverlayChart = () => {
        const geometry = getGeometry(overlayChart);
        const xOf = (index) => geometry.left + (index + 0.5) * (geometry.innerWidth / periods.length);
        const rawMax = Math.max(
            metric === 'ratio' ? 1.1 : 0,
            ...focusedInverters.flatMap((inverter) => periods.map((period) => getMetricValue(getValues(inverter, period.key), metric)))
        );
        const maxValue = metric === 'ratio' ? Math.ceil(rawMax * 10) / 10 : getNiceMax(rawMax);
        const yOf = (value) => geometry.top + geometry.innerHeight - (maxValue === 0 ? 0 : (value / maxValue) * geometry.innerHeight);
        const formatTick = (value) => (metric === 'ratio' ? `${Math.round(value * 100)}%` : formatInteger(value));
        const endLabels = focusedInverters
            .map((inverter) => ({ inverter, y: yOf(getMetricValue(getValues(inverter, periods[periods.length - 1].key), metric)) }))
            .sort((left, right) => left.y - right.y);

        for (let index = 1; index < endLabels.length; index += 1) {
            if (endLabels[index].y - endLabels[index - 1].y < 15) {
                endLabels[index].y = endLabels[index - 1].y + 15;
            }
        }

        return (
            <svg
                className={`falhas-biss-compare-svg is-overlay${hotIndex !== null ? ' has-hot' : ''}`}
                viewBox={`0 0 ${geometry.width} ${geometry.height}`}
                role='img'
                aria-label={`${metricOptions.find((option) => option.value === metric).label} por inversor`}
            >
                {yTickRatios.map((ratio) => (
                    <g key={ratio}>
                        <line className='falhas-biss-compare-grid' x1={geometry.left} x2={geometry.width - geometry.right} y1={yOf(maxValue * ratio)} y2={yOf(maxValue * ratio)} />
                        <text x={geometry.left - 6} y={yOf(maxValue * ratio) + 4} textAnchor='end'>{formatTick(maxValue * ratio)}</text>
                    </g>
                ))}
                {metric === 'ratio' && (
                    <g>
                        <line className='falhas-biss-compare-reference' x1={geometry.left} x2={geometry.width - geometry.right} y1={yOf(1)} y2={yOf(1)} />
                        <text x={geometry.width - geometry.right + 6} y={yOf(1) + 4}>100% do estimado</text>
                    </g>
                )}
                {focusedInverters.map((inverter) => {
                    const color = slotColor(inverter.slot);
                    const path = periods.map((period, index) => (
                        `${index === 0 ? 'M' : 'L'} ${xOf(index)} ${yOf(getMetricValue(getValues(inverter, period.key), metric))}`
                    )).join(' ');
                    const endLabel = endLabels.find((entry) => entry.inverter === inverter);
                    const totalLabel = metric === 'ratio'
                        ? formatPercentRatio(inverter.ratio)
                        : `${formatInteger(inverter.totals[metric])} kWh`;

                    return (
                        <g key={inverter.id}>
                            <path className='falhas-biss-compare-series' d={path} style={{ stroke: color }} />
                            {periods.map((period) => (
                                inverter.faultPeriods.has(period.key)
                                    ? (
                                        <circle
                                            className='falhas-biss-compare-dot'
                                            key={period.key}
                                            cx={xOf(period.index)}
                                            cy={yOf(getMetricValue(getValues(inverter, period.key), metric))}
                                            r='4'
                                            style={{ fill: color }}
                                        />
                                    )
                                    : null
                            ))}
                            <text className='falhas-biss-compare-end-label' x={geometry.width - geometry.right + 6} y={endLabel.y + 4}>
                                {inverter.nome} · {totalLabel}
                            </text>
                        </g>
                    );
                })}
                {hotPeriod && (
                    <g>
                        <line className='falhas-biss-compare-crosshair' x1={xOf(hotIndex)} x2={xOf(hotIndex)} y1={geometry.top} y2={geometry.top + geometry.innerHeight} />
                        {focusedInverters.map((inverter) => (
                            <circle
                                className='falhas-biss-compare-dot'
                                key={inverter.id}
                                cx={xOf(hotIndex)}
                                cy={yOf(getMetricValue(getValues(inverter, hotPeriod.key), metric))}
                                r='5'
                                style={{ fill: slotColor(inverter.slot) }}
                            />
                        ))}
                    </g>
                )}
                {renderPeriodLabels(geometry, xOf)}
                {renderHitAreas(geometry)}
            </svg>
        );
    };

    const renderHeatmap = () => (
        <Box className='falhas-biss-compare-heat' style={{ '--periods': periods.length }}>
            <Box className='falhas-biss-compare-heat-row is-head'>
                <span />
                {periods.map((period) => (
                    <span className={hotIndex === period.index ? 'is-hot' : ''} key={period.key}>
                        {period.showLabel ? period.label : ''}
                    </span>
                ))}
                <span className='falhas-biss-compare-heat-total'>Perda no período</span>
            </Box>
            {inverters.map((inverter) => {
                const focusEntry = focused.find((entry) => entry.id === inverter.id);
                const lossRatio = inverter.totals.estimado > 0 ? inverter.totals.perda / inverter.totals.estimado : 0;

                return (
                    <Box className='falhas-biss-compare-heat-row' key={inverter.id}>
                        <button
                            type='button'
                            className={`falhas-biss-compare-heat-label${focusEntry ? ' is-focused' : ''}`}
                            style={focusEntry ? { '--inv-color': slotColor(focusEntry.slot) } : undefined}
                            aria-pressed={Boolean(focusEntry)}
                            title={focusEntry ? 'Remover dos gráficos' : 'Adicionar aos gráficos'}
                            onClick={() => toggleFocus(inverter)}
                        >
                            <i />
                            <strong>{inverter.nome}</strong>
                            <small>{inverter.serial}</small>
                        </button>
                        {periods.map((period) => {
                            const values = getValues(inverter, period.key);
                            const ratio = values.estimado > 0 ? Math.min(1, getPlotValue(values.perda) / values.estimado) : 0;
                            const hasFault = inverter.faultPeriods.has(period.key);

                            return (
                                <span
                                    className={`falhas-biss-compare-cell${hasFault ? ' is-fault' : ''}${hotIndex === period.index ? ' is-hot' : ''}`}
                                    key={period.key}
                                    style={{ '--alpha': ratio < 0.02 ? 0 : (0.12 + ratio * 0.85).toFixed(2) }}
                                    title={`${inverter.nome} · ${period.tooltip} · perda ${formatDecimal(values.perda)} kWh (${formatPercentRatio(ratio)})${hasFault ? ' · falha cadastrada' : ''}`}
                                    onMouseEnter={() => setHotIndex(period.index)}
                                />
                            );
                        })}
                        <span className='falhas-biss-compare-heat-total'>
                            <strong>{formatDecimal(inverter.totals.perda)} kWh</strong>
                            <small>{formatPercentRatio(lossRatio)} do estimado</small>
                        </span>
                    </Box>
                );
            })}
        </Box>
    );

    return (
        <Box className='falhas-biss-compare'>
            <Box className='falhas-biss-compare-chips' aria-label='Inversores nos gráficos'>
                {focusedInverters.map((inverter) => (
                    <span className='falhas-biss-compare-chip' key={inverter.id} style={{ '--inv-color': slotColor(inverter.slot) }}>
                        <i />
                        {inverter.nome}
                        <small>{inverter.serial}</small>
                        <button type='button' aria-label={`Remover ${inverter.nome} dos gráficos`} onClick={() => toggleFocus(inverter)}>×</button>
                    </span>
                ))}
                <span className='falhas-biss-compare-chip-hint'>
                    {focusedInverters.length >= maxFocusedInverters
                        ? `Limite de ${maxFocusedInverters} nos gráficos. Clique em um inversor no mapa de perdas para trocar.`
                        : 'Clique em um inversor no mapa de perdas para adicionar aos gráficos.'}
                </span>
                {notice && <span className='falhas-biss-compare-notice' role='status'>{notice}</span>}
            </Box>

            <Box className='falhas-biss-compare-cards' style={{ '--cards': Math.max(2, focusedInverters.length) }}>
                {focusedInverters.map((inverter) => (
                    <Box className='falhas-biss-compare-card' key={inverter.id} style={{ '--inv-color': slotColor(inverter.slot) }}>
                        <Box className='falhas-biss-compare-card-heading'>
                            <i />
                            <strong>{inverter.nome}</strong>
                            <small>{inverter.serial}</small>
                        </Box>
                        <Box className='falhas-biss-compare-card-ratio'>
                            <strong>{formatPercentRatio(inverter.ratio)}</strong>
                            <span>real ÷ estimado</span>
                            <span className={`falhas-biss-compare-pill ${getRatioStatus(inverter.ratio)}`} />
                        </Box>
                        <Box className='falhas-biss-compare-bar' aria-hidden='true'>
                            <i style={{ width: `${Math.min(1, inverter.ratio) * 100}%` }} />
                        </Box>
                        <dl>
                            <div><dt>Geração</dt><dd>{formatDecimal(inverter.totals.geracao)} kWh</dd></div>
                            <div><dt>Perda</dt><dd className='is-loss'>{formatDecimal(inverter.totals.perda)} kWh</dd></div>
                            <div><dt>Dias com falha</dt><dd>{formatInteger(inverter.faultDays.size)}</dd></div>
                            <div><dt>Alertas sem falha</dt><dd>{formatInteger(inverter.alertCount)}</dd></div>
                        </dl>
                    </Box>
                ))}
            </Box>

            <Box className='falhas-biss-chart-panel' onMouseLeave={() => setHotIndex(null)}>
                <Box className='falhas-biss-section-heading'>
                    <span><InsightsRoundedIcon /> Geração, perda e estimado por inversor</span>
                    <Box className='falhas-biss-chart-controls'>
                        <Box className='falhas-biss-chart-legend'>
                            {view === 'multiples'
                                ? (
                                    <>
                                        <span className='is-inverter'>Geração (cor do inversor)</span>
                                        <span className='is-loss'>Perda</span>
                                        <span className='is-estimated'>Estimado</span>
                                        <span className='is-fault'>Falha cadastrada</span>
                                    </>
                                )
                                : (
                                    <>
                                        {focusedInverters.map((inverter) => (
                                            <span className='is-series' key={inverter.id} style={{ '--inv-color': slotColor(inverter.slot) }}>{inverter.nome}</span>
                                        ))}
                                        {metric === 'ratio' && <span className='is-estimated'>100% do estimado</span>}
                                        <span className='is-fault-dot'>Falha cadastrada</span>
                                    </>
                                )}
                        </Box>
                        {view === 'overlay' && (
                            <Box className='falhas-biss-compare-toggle' role='tablist' aria-label='Métrica sobreposta'>
                                {metricOptions.map((option) => (
                                    <button
                                        type='button'
                                        role='tab'
                                        key={option.value}
                                        className={metric === option.value ? 'is-active' : ''}
                                        aria-selected={metric === option.value}
                                        onClick={() => setMetric(option.value)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </Box>
                        )}
                        <Box className='falhas-biss-compare-toggle' role='tablist' aria-label='Disposição dos gráficos'>
                            <button type='button' role='tab' className={view === 'multiples' ? 'is-active' : ''} aria-selected={view === 'multiples'} onClick={() => handleViewChange('multiples')}>Lado a lado</button>
                            <button type='button' role='tab' className={view === 'overlay' ? 'is-active' : ''} aria-selected={view === 'overlay'} onClick={() => handleViewChange('overlay')}>Sobreposto</button>
                        </Box>
                        <Box className='falhas-biss-chart-grouping'>
                            <span className={isMonthly ? '' : 'is-active'}>Diário</span>
                            <SettaSwitch
                                checked={isMonthly}
                                className='is-compact'
                                ariaLabel='Alternar entre agrupamento diário e mensal'
                                onChange={handleGroupingChange}
                            />
                            <span className={isMonthly ? 'is-active' : ''}>Mensal</span>
                        </Box>
                    </Box>
                </Box>

                {renderTooltip()}

                {view === 'multiples'
                    ? <Box className='falhas-biss-compare-multiples'>{focusedInverters.map(renderSmallChart)}</Box>
                    : renderOverlayChart()}
            </Box>

            <Box className='falhas-biss-chart-panel' onMouseLeave={() => setHotIndex(null)}>
                <Box className='falhas-biss-section-heading'>
                    <span><GridOnRoundedIcon /> Mapa de perdas · {formatInteger(inverters.length)} inversores</span>
                    <Box className='falhas-biss-compare-heat-legend'>
                        <span>Perda em % do estimado</span>
                        <span className='falhas-biss-compare-ramp'>
                            {[0, 0.2, 0.4, 0.65, 0.9].map((alpha) => <i key={alpha} style={{ '--alpha': alpha }} />)}
                        </span>
                        <span>0% → 100%</span>
                        <span className='is-fault'>Falha cadastrada</span>
                    </Box>
                </Box>
                {renderHeatmap()}
            </Box>
        </Box>
    );
};

export default FalhasBissCompare;
